/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useConferencesList.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing conferences list with DataGrid integration.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { fetchConferences } from '../api/conferencesApi';

interface UseConferencesListOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialColumns?: any[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
  domainId?: number;
}

export function useConferencesList(options: UseConferencesListOptions = {}) {
  const {
    initialColumns = [],
    initialSortBy = 'extension',
    initialSortOrder = 'asc',
    initialLimit = 10,
    domainId
  } = options;

  const sortableColumns = useMemo(() => new Set([
    'extension',
    'name',
    'description',
    'enabled',
    'created_at',
  ]), []);

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    const safeSortBy = params.sortBy && sortableColumns.has(params.sortBy)
      ? params.sortBy
      : initialSortBy;
    const safeSortOrder = params.sortOrder === 'desc' ? 'desc' : 'asc';

    const response = await fetchConferences({
      page: params.page,
      limit: params.limit,
      sortBy: safeSortBy,
      sortOrder: safeSortOrder,
      search: params.search?.trim(),
      domainId,
    });
    return {
      data: response.data || [],
      pagination: response.pagination || {
        page: params.page,
        limit: params.limit,
        total: 0,
        totalPages: 0
      }
    } as DataGridFetchResult;
  }, [sortableColumns, initialSortBy, domainId]);

  const dataGrid = useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return dataGrid;
}

